import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import PresenceDot, { PRESENCE_SCOPE } from "@/components/PresenceDot";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, User } from "lucide-react";
import RoleBadge from "@/components/RoleBadge";

export default function WorkspaceSwitcher({ variant = "desktop" }) {
  const { user, refresh } = useAuth();
  const { onlineIds } = useRealtime();
  const [workspaces, setWorkspaces] = useState([]);
  const [members, setMembers] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/workspaces").then(({ data }) => setWorkspaces(data.workspaces)).catch(() => {});
  }, [user?.workspace_id]);

  useEffect(() => {
    if (!user?.workspace_id) { setMembers([]); return; }
    api.get(`/workspaces/${user.workspace_id}`)
      .then(({ data }) => setMembers(data.members || []))
      .catch(() => setMembers([]));
  }, [user?.workspace_id]);

  const change = async (v) => {
    if (v === "__manage") { nav("/team"); return; }
    try {
      await api.post("/workspaces/switch", { workspace_id: v === "personal" ? null : v });
      await refresh();
      const ws = workspaces.find((w) => w.id === v);
      toast.success(ws ? `Switched to ${ws.name}` : "Switched to personal space");
      window.location.reload();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const current = user?.workspace_id || "personal";
  const role = user?.workspace_id ? user.workspace_role : null;
  const onlineMembers = members.filter((m) => onlineIds.includes(m.id));
  return (
    <div className="px-3 pt-3 space-y-1.5">
      <Select value={current} onValueChange={change}>
        <SelectTrigger className="h-9 bg-background/60" data-testid={variant === "mobile" ? "workspace-switcher-mobile" : "workspace-switcher"}>
          <div className="flex items-center gap-2 truncate">
            {current === "personal" ? <User className="h-3.5 w-3.5 text-muted-foreground" /> : <Users className="h-3.5 w-3.5 text-primary" />}
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="personal" data-testid="workspace-option-personal">Personal space</SelectItem>
          {workspaces.map((w) => (
            <SelectItem key={w.id} value={w.id} data-testid={`workspace-option-${w.id}`}>{w.name}</SelectItem>
          ))}
          <SelectItem value="__manage" data-testid="workspace-option-manage" className="text-primary">Manage teams…</SelectItem>
        </SelectContent>
      </Select>
      {role && (
        <div className="flex items-center justify-between px-1" data-testid="sidebar-role">
          <RoleBadge role={role} testid={`sidebar-role-badge-${role}`} />
          {role === "viewer" && <span className="text-[10px] text-muted-foreground">read-only</span>}
        </div>
      )}
      {user?.workspace_id && onlineMembers.length > 0 && (
        <div className="flex items-center gap-1.5 px-1 pt-0.5" data-testid="presence-online-cluster">
          <div className="flex -space-x-1.5">
            {onlineMembers.slice(0, 5).map((m) => (
              <div
                key={m.id}
                title={`${m.name}${m.id === user.id ? " (you)" : ""} · présent dans l’application`}
                data-testid={`presence-avatar-${m.id}`}
                className="relative flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-primary font-semibold text-[9px] ring-2 ring-background"
              >
                {m.name?.[0]?.toUpperCase() || "U"}
                <PresenceDot online testid={`presence-dot-switcher-${m.id}`} className="h-2 w-2 -bottom-0 -right-0" />
              </div>
            ))}
          </div>
          <span className="text-[10px] text-muted-foreground" data-testid="presence-online-count" title={PRESENCE_SCOPE}>
            {onlineMembers.length} dans l’app
          </span>
        </div>
      )}
    </div>
  );
}
